import React from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';

export type ExportFormat = 'xlsx' | 'csv';

export class UserFacingExportError extends Error {}

interface Props {
  /** Performs the download. Rejects on failure; this component owns the message. */
  onExport: (format: ExportFormat) => Promise<void>;
  disabled?: boolean;
}

/**
 * Icon-only Export button with an Excel/CSV menu.
 *
 * Two clicks to a file. A modal with a radio group and a confirm button would
 * be three, which is why AdoptionReportExportFormatDialog is not reused here.
 */
export function ExportMenuButton({ onExport, disabled }: Props) {
  const [isExporting, setIsExporting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const run = async (format: ExportFormat) => {
    setError(null);
    setIsExporting(true);
    try {
      await onExport(format);
    } catch (err) {
      // Never surface the raw server string: a 5xx body can carry a stack
      // trace or a column name. The caller has already routed any surfaceable
      // 4xx detail through getUserFacingApiMessageAsync.
      console.error('Export failed', err);
      setError(
        err instanceof UserFacingExportError
          ? err.message
          : 'Unable to export. Please try again.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Popover
      open={Boolean(error)}
      onOpenChange={open => !open && setError(null)}
    >
      <PopoverAnchor asChild>
        <div className='shrink-0'>
          <DropdownMenu>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type='button'
                      variant='outline'
                      size='iconMd'
                      aria-label='Export'
                      disabled={disabled || isExporting}
                    >
                      {isExporting ? (
                        <Loader2 className='animate-spin' aria-hidden='true' />
                      ) : (
                        <Download aria-hidden='true' />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                {!error && (
                  <TooltipContent side='bottom'>Export</TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onClick={() => void run('xlsx')}>
                <FileSpreadsheet />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => void run('csv')}>
                <FileText />
                CSV (.csv)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </PopoverAnchor>
      {error ? (
        <PopoverContent
          asChild
          align='end'
          sideOffset={8}
          collisionPadding={16}
          onOpenAutoFocus={event => event.preventDefault()}
          onFocusOutside={event => event.preventDefault()}
          onCloseAutoFocus={event => event.preventDefault()}
        >
          <Alert
            role='alert'
            variant='destructive'
            className='w-72 max-w-[calc(100vw-2rem)]'
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </PopoverContent>
      ) : null}
    </Popover>
  );
}
